import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { ApiError, api } from '../../lib/api';
import { queryRoots } from '../../lib/events';
import type { ReservationItem } from '../reservations/index';
import type {
  AfterSalesHandoverOptions,
  BoardResponse,
  CancelBody,
  CancellableDeal,
  CommissionSettings,
  CommissionView,
  CompleteInvoiceBody,
  CompleteInvoiceResponse,
  CreateDealBody,
  CreateOptions,
  DealAppointmentsResponse,
  DealDocumentsResponse,
  DealFilter,
  DealFinancingsResponse,
  DealRecord,
  DealUnitsResponse,
  FiltersResponse,
  HistoryKind,
  HistoryResponse,
  InvoiceRow,
  InvoicesResponse,
  ListResponse,
  ListSort,
  ListStatus,
  LoseBody,
  LostReason,
  PartnerCommissionsResponse,
  PipelineInfo,
  RoleDefault,
  StageBody,
  StageOptionsResponse,
  UnitChangeBody,
  UnitChangePreview,
  UserCommissionSettings,
  UserRef,
} from './types';

/** Query keys of the module under the roots of `lib/events.ts` so realtime events refresh them. */
export const dealKeys = {
  board: (params: Record<string, string>) => [...queryRoots.deals, 'board', params] as const,
  filters: () => [...queryRoots.deals, 'filters'] as const,
  pipelines: () => [...queryRoots.deals, 'pipelines'] as const,
  pipeline: (id: string | null) => [...queryRoots.deals, 'pipeline', id ?? ''] as const,
  list: (params: Record<string, string>) => [...queryRoots.deals, 'list', params] as const,
  createOptions: (contactId: string) => [...queryRoots.deals, 'create-options', contactId] as const,
  lostReasons: () => [...queryRoots.deals, 'lost-reasons'] as const,
  cancellable: (q: string) => [...queryRoots.deals, 'cancellable', q] as const,
  invoices: () => [...queryRoots.deals, 'invoices'] as const,
  commissions: (year: number | null) => [...queryRoots.deals, 'commissions', year ?? 'current'] as const,
  settings: () => [...queryRoots.deals, 'commission-settings'] as const,
  userSettings: (userId: string) => [...queryRoots.deals, 'commission-settings', 'user', userId] as const,
  record: (id: string) => [...queryRoots.deal, id] as const,
  stageOptions: (id: string) => [...queryRoots.deal, id, 'stage-options'] as const,
  afterSalesHandover: (id: string) => [...queryRoots.deal, id, 'after-sales-handover'] as const,
  appointments: (id: string) => [...queryRoots.deal, id, 'appointments'] as const,
  units: (id: string) => [...queryRoots.deal, id, 'units'] as const,
  unitChangePreview: (id: string, toUnitId: string, fromUnitId: string | null) =>
    [...queryRoots.deal, id, 'units', 'change-preview', toUnitId, fromUnitId ?? ''] as const,
  financings: (id: string) => [...queryRoots.deal, id, 'financings'] as const,
  documents: (id: string) => [...queryRoots.deal, id, 'documents'] as const,
  commission: (id: string) => [...queryRoots.deal, id, 'commission'] as const,
  history: (id: string, kind: HistoryKind) => [...queryRoots.deal, id, 'history', kind] as const,
};

function query(params: Record<string, string | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) if (value) search.set(key, value);
  const text = search.toString();
  return text ? `?${text}` : '';
}

/** The query parameters of a filter (only the set ones, booleans as `1`). */
export function filterParams(filter: DealFilter): Record<string, string> {
  const params: Record<string, string> = {};
  if (filter.advisorId) params['advisorId'] = filter.advisorId;
  if (filter.propertyId) params['propertyId'] = filter.propertyId;
  if (filter.referrerPartnerId) params['referrerPartnerId'] = filter.referrerPartnerId;
  if (filter.q) params['q'] = filter.q;
  if (filter.stageAgeOver) params['stageAgeOver'] = filter.stageAgeOver;
  if (filter.gateOnly) params['gateOnly'] = '1';
  if (filter.overdueOnly) params['overdueOnly'] = '1';
  if (filter.noNextStep) params['noNextStep'] = '1';
  if (filter.mine) params['mine'] = '1';
  if (filter.includeCancelled) params['includeCancelled'] = '1';
  return params;
}

function notFoundOrForbidden(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 403 || error.status === 404);
}

// --- board, list, filters --------------------------------------------------------------------------

export function useBoard(filter: DealFilter, late: 'collapsed' | 'expanded', enabled = true) {
  const params = { ...filterParams(filter), late };
  return useQuery({
    queryKey: dealKeys.board(params),
    queryFn: ({ signal }) =>
      api<BoardResponse>(`/deals/board${query({ pipeline: 'property', ...params })}`, { signal }),
    placeholderData: keepPreviousData,
    enabled,
    staleTime: 15_000,
  });
}

/** The live count of the filter flyout („ergibt 6 Deals"): the board with the pending filters. */
export function useBoardPreview(filter: DealFilter, enabled: boolean) {
  const params = { ...filterParams(filter), late: 'expanded', preview: '1' };
  return useQuery({
    queryKey: dealKeys.board(params),
    queryFn: ({ signal }) =>
      api<BoardResponse>(
        `/deals/board${query({ pipeline: 'property', ...filterParams(filter), late: 'expanded' })}`,
        {
          signal,
        },
      ),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
}

export function useDealFilters(enabled = true) {
  return useQuery({
    queryKey: dealKeys.filters(),
    queryFn: ({ signal }) => api<FiltersResponse>('/deals/filters', { signal }),
    enabled,
    staleTime: 60_000,
  });
}

export function usePipelines(enabled = true) {
  return useQuery({
    queryKey: dealKeys.pipelines(),
    queryFn: ({ signal }) => api<{ property: { id: string; name: string } }>('/deals/pipelines', { signal }),
    enabled,
    staleTime: 300_000,
  });
}

export function usePipeline(id: string | null, enabled: boolean) {
  return useQuery({
    queryKey: dealKeys.pipeline(id),
    queryFn: ({ signal }) => api<PipelineInfo>(`/deals/pipelines/${id ?? ''}`, { signal }),
    enabled: enabled && id !== null,
    staleTime: 60_000,
  });
}

export const LIST_PAGE_SIZE = 50;

export function useDealList(filter: DealFilter, status: ListStatus, sort: ListSort) {
  const params = { ...filterParams(filter), status, sort };
  return useInfiniteQuery({
    queryKey: dealKeys.list(params),
    queryFn: ({ pageParam, signal }) =>
      api<ListResponse>(
        `/deals${query({ ...params, cursor: pageParam ?? undefined, limit: String(LIST_PAGE_SIZE) })}`,
        { signal },
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
}

// --- create ----------------------------------------------------------------------------------------

export function useCreateOptions(contactId: string | null) {
  return useQuery({
    queryKey: dealKeys.createOptions(contactId ?? ''),
    queryFn: ({ signal }) =>
      api<CreateOptions>(`/deals/create-options${query({ contactId: contactId ?? undefined })}`, { signal }),
    enabled: contactId !== null,
    retry: (count, error) => !notFoundOrForbidden(error) && count < 1,
  });
}

export function createDeal(body: CreateDealBody): Promise<DealRecord> {
  return api<DealRecord>('/deals', { method: 'POST', body });
}

// --- record and tabs -------------------------------------------------------------------------------

export function useDealRecord(id: string): UseQueryResult<DealRecord, Error> {
  return useQuery({
    queryKey: dealKeys.record(id),
    queryFn: ({ signal }) => api<DealRecord>(`/deals/${id}`, { signal }),
    retry: (count, error) => !notFoundOrForbidden(error) && count < 1,
    staleTime: 15_000,
  });
}

export function useStageOptions(id: string | null, enabled = true) {
  return useQuery({
    queryKey: dealKeys.stageOptions(id ?? ''),
    queryFn: ({ signal }) => api<StageOptionsResponse>(`/deals/${id ?? ''}/stage-options`, { signal }),
    enabled: enabled && id !== null,
    staleTime: 15_000,
  });
}

export function useLostReasons(enabled: boolean) {
  return useQuery({
    queryKey: dealKeys.lostReasons(),
    queryFn: ({ signal }) => api<{ items: LostReason[] }>('/deals/lost-reasons', { signal }),
    enabled,
    staleTime: 300_000,
  });
}

export function useDealAppointments(id: string, enabled: boolean) {
  return useQuery({
    queryKey: dealKeys.appointments(id),
    queryFn: ({ signal }) => api<DealAppointmentsResponse>(`/deals/${id}/appointments`, { signal }),
    enabled,
    staleTime: 15_000,
  });
}

export function useDealUnits(id: string, enabled: boolean) {
  return useQuery({
    queryKey: dealKeys.units(id),
    queryFn: ({ signal }) => api<DealUnitsResponse>(`/deals/${id}/units`, { signal }),
    enabled,
    staleTime: 15_000,
  });
}

export function useUnitChangePreview(id: string, toUnitId: string | null, fromUnitId: string | null) {
  return useQuery({
    queryKey: dealKeys.unitChangePreview(id, toUnitId ?? '', fromUnitId),
    queryFn: ({ signal }) =>
      api<UnitChangePreview>(
        `/deals/${id}/units/change-preview${query({ toUnitId: toUnitId ?? undefined, fromUnitId: fromUnitId ?? undefined })}`,
        { signal },
      ),
    enabled: toUnitId !== null,
    retry: false,
    staleTime: 15_000,
  });
}

export function useDealFinancings(id: string, enabled: boolean) {
  return useQuery({
    queryKey: dealKeys.financings(id),
    queryFn: ({ signal }) => api<DealFinancingsResponse>(`/deals/${id}/financings`, { signal }),
    enabled,
    staleTime: 15_000,
  });
}

export function useDealDocuments(id: string, enabled: boolean) {
  return useQuery({
    queryKey: dealKeys.documents(id),
    queryFn: ({ signal }) => api<DealDocumentsResponse>(`/deals/${id}/documents`, { signal }),
    enabled,
    staleTime: 15_000,
  });
}

export function useDealCommission(id: string, enabled: boolean) {
  return useQuery({
    queryKey: dealKeys.commission(id),
    queryFn: ({ signal }) => api<CommissionView>(`/deals/${id}/commission`, { signal }),
    enabled,
    staleTime: 15_000,
  });
}

export function useDealHistory(id: string, kind: HistoryKind, enabled: boolean) {
  return useQuery({
    queryKey: dealKeys.history(id, kind),
    queryFn: ({ signal }) => api<HistoryResponse>(`/deals/${id}/history${query({ kind })}`, { signal }),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
}

// --- transitions -----------------------------------------------------------------------------------

export function changeStage(id: string, body: StageBody): Promise<DealRecord> {
  return api<DealRecord>(`/deals/${id}/stage`, { method: 'POST', body });
}

export function loseDeal(id: string, body: LoseBody): Promise<DealRecord> {
  return api<DealRecord>(`/deals/${id}/lose`, { method: 'POST', body });
}

export function reopenDeal(id: string, reason: string): Promise<{ deal: DealRecord; notice: string | null }> {
  return api<{ deal: DealRecord; notice: string | null }>(`/deals/${id}/reopen`, {
    method: 'POST',
    body: { reason },
  });
}

export function changeUnit(
  id: string,
  body: UnitChangeBody,
): Promise<{ deal: DealRecord; preview: UnitChangePreview }> {
  return api<{ deal: DealRecord; preview: UnitChangePreview }>(`/deals/${id}/units/change`, {
    method: 'POST',
    body,
  });
}

export function addUnit(id: string, unitId: string): Promise<DealRecord> {
  return api<DealRecord>(`/deals/${id}/units`, { method: 'POST', body: { unitId } });
}

export function cancelDeal(id: string, body: CancelBody): Promise<DealRecord> {
  return api<DealRecord>(`/deals/${id}/cancel`, { method: 'POST', body });
}

export function useCancellableDeals(q: string, enabled: boolean) {
  return useQuery({
    queryKey: dealKeys.cancellable(q),
    queryFn: ({ signal }) =>
      api<{ items: CancellableDeal[] }>(`/deals/cancellable${query({ q })}`, { signal }),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
}

/** Deal-Pipeline 12: „Schreibrecht anfragen" → the access approval for the deals write right. */
export function requestDealsWriteAccess(): Promise<void> {
  return api<void>('/approvals/access', { method: 'POST', body: { kind: 'tool', tool: 'deals_write' } });
}

export function setDocumentVisibility(documentId: string, visibleToCustomer: boolean): Promise<unknown> {
  return api<unknown>(`/documents/${documentId}/visibility`, {
    method: 'PATCH',
    body: { visibleToCustomer },
  });
}

// --- invoicing and commissions ---------------------------------------------------------------------

export function useInvoices(enabled: boolean) {
  return useQuery({
    queryKey: dealKeys.invoices(),
    queryFn: ({ signal }) => api<InvoicesResponse>('/deals/invoices', { signal }),
    enabled,
    retry: (count, error) => !notFoundOrForbidden(error) && count < 1,
    staleTime: 15_000,
  });
}

export function issueInvoice(
  id: string,
  body: { invoiceNumber: string; invoicedOn: string; dueOn: string },
): Promise<InvoiceRow> {
  return api<InvoiceRow>(`/deals/invoices/${id}/issue`, { method: 'POST', body });
}

/** Deal-Pipeline 22 „Übergabe an": the handover stage, the candidates and the preselection. */
export function useAfterSalesHandover(dealId: string | null) {
  return useQuery({
    queryKey: dealKeys.afterSalesHandover(dealId ?? ''),
    queryFn: ({ signal }) =>
      api<AfterSalesHandoverOptions>(`/deals/${dealId ?? ''}/after-sales-handover`, { signal }),
    enabled: dealId !== null,
    staleTime: 0,
  });
}

/** „Abschluss bestätigen": commission receipt + after-sales owner + stage move in one transaction. */
export function completeInvoice(id: string, body: CompleteInvoiceBody): Promise<CompleteInvoiceResponse> {
  return api<CompleteInvoiceResponse>(`/deals/invoices/${id}/complete`, { method: 'POST', body });
}

export function cancelInvoice(id: string, reason: string): Promise<InvoiceRow> {
  return api<InvoiceRow>(`/deals/invoices/${id}/cancel`, { method: 'POST', body: { reason } });
}

export function usePartnerCommissions(year: number | null, enabled: boolean) {
  return useQuery({
    queryKey: dealKeys.commissions(year),
    queryFn: ({ signal }) =>
      api<PartnerCommissionsResponse>(
        `/deals/commissions${query({ year: year === null ? undefined : String(year) })}`,
        {
          signal,
        },
      ),
    enabled,
    placeholderData: keepPreviousData,
    retry: (count, error) => !notFoundOrForbidden(error) && count < 1,
    staleTime: 15_000,
  });
}

export function markSharePaidOut(
  shareId: string,
  paidOutOn: string | null,
): Promise<{ id: string; paidOutAt: string; paidOutBy: UserRef | null }> {
  return api<{ id: string; paidOutAt: string; paidOutBy: UserRef | null }>(
    `/commissions/shares/${shareId}/paid-out`,
    {
      method: 'POST',
      body: paidOutOn === null ? {} : { paidOutOn },
    },
  );
}

// --- settings --------------------------------------------------------------------------------------

export function useCommissionSettings(enabled = true) {
  return useQuery({
    queryKey: dealKeys.settings(),
    queryFn: ({ signal }) => api<CommissionSettings>('/settings/commission', { signal }),
    enabled,
    retry: (count, error) => !notFoundOrForbidden(error) && count < 1,
    staleTime: 15_000,
  });
}

export function patchPropertyRate(
  propertyId: string,
  body: { ratePct?: number | null | undefined; capPerDealCents?: number | null | undefined },
): Promise<CommissionSettings> {
  return api<CommissionSettings>(`/settings/commission/properties/${propertyId}`, { method: 'PATCH', body });
}

export function putRoleDefaults(
  body: Pick<RoleDefault, 'role' | 'splitPct' | 'capPct'>[],
): Promise<CommissionSettings> {
  return api<CommissionSettings>('/settings/commission/role-defaults', { method: 'PUT', body });
}

export function useUserCommissionSettings(userId: string | null) {
  return useQuery({
    queryKey: dealKeys.userSettings(userId ?? ''),
    queryFn: ({ signal }) =>
      api<UserCommissionSettings>(`/settings/commission/users/${userId ?? ''}`, { signal }),
    enabled: userId !== null,
    staleTime: 15_000,
  });
}

export function putUserCommissionSettings(
  userId: string,
  roles: { role: RoleDefault['role']; splitPct: number | null; capPct: number | null }[],
): Promise<UserCommissionSettings> {
  return api<UserCommissionSettings>(`/settings/commission/users/${userId}`, {
    method: 'PUT',
    body: { roles },
  });
}

/** Refreshes everything of the deals module after a mutation (the realtime event does the same). */
export function useInvalidateDeals() {
  const queryClient = useQueryClient();
  return async (dealId?: string) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [...queryRoots.deals] }),
      dealId
        ? queryClient.invalidateQueries({ queryKey: dealKeys.record(dealId) })
        : queryClient.invalidateQueries({ queryKey: [...queryRoots.deal] }),
      queryClient.invalidateQueries({ queryKey: [...queryRoots.tasks] }),
      queryClient.invalidateQueries({ queryKey: [...queryRoots.contact] }),
    ]);
  };
}

/** Downloads a CSV export of the module (`GET /deals/invoices/export`, `GET /deals/commissions/export`) through a same-origin link. */
export function downloadCsv(path: string): void {
  const anchor = document.createElement('a');
  anchor.href = `/api${path}`;
  anchor.download = '';
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

/** The reservations of a deal (`GET /reservations?dealId=`), keyed under the reservations root so the reservations module's events refresh them. */
export function useDealReservations(dealId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...queryRoots.reservations, 'list', { dealId }] as const,
    queryFn: ({ signal }) =>
      api<{ items: ReservationItem[]; total: number }>(`/reservations${query({ dealId })}`, { signal }),
    enabled,
    staleTime: 15_000,
  });
}

/** The document rows of the deal from the documents module (`GET /documents?dealId=`), for the visibility toggles of Deal-Akte 07. */
export function useDealDocumentRows(dealId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...queryRoots.documents, 'deal', dealId] as const,
    queryFn: ({ signal }) =>
      api<{
        items: {
          id: string;
          visibleToCustomer: boolean;
          visibilityLocked: boolean;
          kind: 'document' | 'request';
        }[];
      }>(`/documents${query({ dealId })}`, { signal }),
    enabled,
    staleTime: 15_000,
  });
}
